import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { customerApi, venueApi, smsApi } from '../api';
import {
  CONTRACT_STATUSES, CONTRACT_STATUS_COLORS,
  CUSTOMER_SOURCES, EVENT_TYPES, EVENT_TYPE_COLORS,
  DATE_CRITERIA, SEARCH_CRITERIA,
} from '../constants';

interface Venue {
  venueIdx: number;
  name: string;
}

interface WHCustomer {
  whCustomerIdx: number;
  groomName?: string;
  groomPhone?: string;
  brideName?: string;
  bridePhone?: string;
  contactName?: string;
  contactPhone?: string;
  eventType?: string;
  contractStatus: string;
  contractDt?: string;
  eventDt?: string;
  eventTime?: string;
  venueName?: string;
  menuName?: string;
  guaranteeCount?: number;
  customerSource?: string;
  staffName?: string;
  vendorName?: string;
  memo?: string;
  createDt: string;
}

export default function WeddingHallCustomers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<WHCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);

  // Filters - Row 1
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [vendorName, setVendorName] = useState('');

  // Filters - Row 2
  const [dateCriteria, setDateCriteria] = useState('createDt');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchCriteria, setSearchCriteria] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Checkbox
  const [checkedIds, setCheckedIds] = useState<number[]>([]);

  // Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [venues, setVenues] = useState<Venue[]>([]);

  const [form, setForm] = useState({
    contractStatus: '홀드',
    eventType: '예식',
    groomName: '', groomPhone: '',
    brideName: '', bridePhone: '',
    contactName: '', contactPhone: '',
    venueIdx: '',
    eventDt: '', eventTime: '',
    menuName: '', mealPrice: '', guaranteeCount: '',
    venueRentalFee: '',
    customerSource: '', vendorName: '',
    memo: '',
  });

  const isWedding = form.eventType === '예식';

  const fetchCustomers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await customerApi.list({
        q: searchQuery, searchCriteria,
        contractStatus: statusFilter || undefined,
        customerSource: sourceFilter || undefined,
        eventType: eventTypeFilter || undefined,
        dateCriteria, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined,
        vendorName: vendorName || undefined,
        page, size: 20,
      });
      setCustomers(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('고객 목록 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, searchCriteria, statusFilter, sourceFilter, eventTypeFilter, dateCriteria, dateFrom, dateTo, vendorName, page]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  useEffect(() => {
    venueApi.list({ status: 'ACTIVE' }).then((data: any) => {
      setVenues(data.items || data || []);
    }).catch(() => {});
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isWedding) {
      if (!form.groomName && !form.brideName) { alert('신랑 또는 신부 이름은 필수입니다.'); return; }
      if (!form.groomPhone && !form.bridePhone) { alert('신랑 또는 신부 연락처는 필수입니다.'); return; }
    } else {
      if (!form.contactName) { alert('담당자명은 필수입니다.'); return; }
      if (!form.contactPhone) { alert('담당자 연락처는 필수입니다.'); return; }
    }
    if (!form.venueIdx) { alert('예식홀을 선택해 주세요.'); return; }
    if (!form.eventDt) { alert('행사일을 입력해 주세요.'); return; }

    try {
      await customerApi.create({
        ...form,
        venueIdx: Number(form.venueIdx),
        mealPrice: form.mealPrice ? Number(form.mealPrice) : undefined,
        guaranteeCount: form.guaranteeCount ? Number(form.guaranteeCount) : undefined,
        venueRentalFee: form.venueRentalFee ? Number(form.venueRentalFee) : undefined,
      });
      alert('고객이 등록되었습니다.');
      setShowAddModal(false);
      setForm({
        contractStatus: '홀드', eventType: '예식',
        groomName: '', groomPhone: '', brideName: '', bridePhone: '',
        contactName: '', contactPhone: '',
        venueIdx: '', eventDt: '', eventTime: '',
        menuName: '', mealPrice: '', guaranteeCount: '',
        venueRentalFee: '',
        customerSource: '', vendorName: '', memo: '',
      });
      fetchCustomers();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  const formatDate = (dt?: string) => {
    if (!dt) return '미정';
    return new Date(dt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      setCheckedIds(customers.map(c => c.whCustomerIdx));
    } else {
      setCheckedIds([]);
    }
  };

  const handleCheck = (id: number, checked: boolean) => {
    if (checked) {
      setCheckedIds(prev => [...prev, id]);
    } else {
      setCheckedIds(prev => prev.filter(i => i !== id));
    }
  };

  const handleSms = (e: React.MouseEvent) => {
    e.stopPropagation();
    alert('문자 발송 기능은 준비 중입니다');
  };

  const handleBulkSms = () => {
    alert('문자 발송 기능은 준비 중입니다');
  };

  const totalPages = Math.ceil(total / 20);

  const getCustomerDisplay = (c: WHCustomer) => {
    if (c.eventType === '예식' || (!c.contactName && (c.groomName || c.brideName))) {
      return (
        <div className="flex flex-col">
          {c.groomName && <span className="text-xs"><i className="ri-men-line text-blue-500 mr-1"></i>{c.groomName}</span>}
          {c.brideName && <span className="text-xs"><i className="ri-women-line text-pink-500 mr-1"></i>{c.brideName}</span>}
        </div>
      );
    }
    return <span className="text-xs">{c.contactName || '-'}</span>;
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">

        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">고객관리</h1>
            <p className="text-sm text-gray-600 mt-1">웨딩홀 고객을 관리합니다</p>
          </div>
          <button onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 cursor-pointer">
            <i className="ri-add-line"></i> 고객 등록
          </button>
        </div>

        {/* 필터 영역 */}
        <div className="bg-white p-5 rounded-lg shadow-sm mb-6">
          {/* Row 1 */}
          <div className="flex items-end gap-4 flex-wrap">
            {/* 계약구분 */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">계약구분</label>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px]">
                <option value="">전체</option>
                {CONTRACT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {/* 고객경로 */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">고객경로</label>
              <select value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px]">
                <option value="">전체</option>
                {CUSTOMER_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            {/* 행사유형 */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">행사유형</label>
              <select value={eventTypeFilter} onChange={(e) => { setEventTypeFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px]">
                <option value="">전체</option>
                {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            {/* 업체명 조회 */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">업체명조회</label>
              <input type="text" placeholder="업체명 검색" value={vendorName}
                onChange={(e) => { setVendorName(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[140px]" />
            </div>
          </div>
          {/* Row 2 */}
          <div className="flex items-end gap-4 flex-wrap mt-3">
            {/* 조회기간 */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">조회기간</label>
              <div className="flex items-center gap-2 flex-wrap">
                {DATE_CRITERIA.map(c => (
                  <label key={c.value} className="flex items-center gap-1 text-xs">
                    <input type="radio" name="dateCriteria" value={c.value} checked={dateCriteria === c.value}
                      onChange={() => { setDateCriteria(c.value); setPage(1); }} className="accent-blue-600" />
                    {c.label}
                  </label>
                ))}
                <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <span className="text-gray-400">~</span>
                <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            {/* 이름조회 */}
            <div className="flex-1 min-w-[250px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">이름조회</label>
              <div className="flex items-center gap-2">
                <select value={searchCriteria} onChange={(e) => { setSearchCriteria(e.target.value); setPage(1); }}
                  className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[90px]">
                  {SEARCH_CRITERIA.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input type="text" placeholder="검색어 입력" value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 검색 결과 카운트 */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-gray-600">
            총 <span className="font-semibold text-gray-800">{total}</span>건
          </div>
        </div>

        {/* 고객 목록 테이블 */}
        {isLoading ? (
          <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : customers.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-16 text-center text-gray-400">
            <i className="ri-building-2-line text-5xl block mb-3"></i>등록된 고객이 없습니다.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs">
                    <th className="px-3 py-3 text-center font-medium w-10">
                      <input type="checkbox"
                        checked={checkedIds.length === customers.length && customers.length > 0}
                        onChange={(e) => handleCheckAll(e.target.checked)}
                        className="accent-blue-600 cursor-pointer" />
                    </th>
                    <th className="px-3 py-3 text-left font-medium">행사일</th>
                    <th className="px-3 py-3 text-center font-medium">시간</th>
                    <th className="px-3 py-3 text-left font-medium">예식홀</th>
                    <th className="px-3 py-3 text-left font-medium">고객명</th>
                    <th className="px-3 py-3 text-center font-medium">메뉴</th>
                    <th className="px-3 py-3 text-center font-medium">보증인원</th>
                    <th className="px-3 py-3 text-center font-medium">DB경로</th>
                    <th className="px-3 py-3 text-left font-medium">담당자</th>
                    <th className="px-3 py-3 text-center font-medium">계약일</th>
                    <th className="px-3 py-3 text-center font-medium">상태</th>
                    <th className="px-3 py-3 text-center font-medium">문자</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {customers.map((c) => (
                    <tr key={c.whCustomerIdx}
                      className="hover:bg-blue-50/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/wedding-hall/customer-detail?id=${c.whCustomerIdx}`)}>
                      <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox"
                          checked={checkedIds.includes(c.whCustomerIdx)}
                          onChange={(e) => handleCheck(c.whCustomerIdx, e.target.checked)}
                          className="accent-blue-600 cursor-pointer" />
                      </td>
                      <td className="px-3 py-3 text-xs font-medium">{formatDate(c.eventDt)}</td>
                      <td className="px-3 py-3 text-center text-xs text-gray-600">{c.eventTime || '-'}</td>
                      <td className="px-3 py-3 text-xs text-gray-600">{c.venueName || '-'}</td>
                      <td className="px-3 py-3">{getCustomerDisplay(c)}</td>
                      <td className="px-3 py-3 text-center text-xs text-gray-600">{c.menuName || '-'}</td>
                      <td className="px-3 py-3 text-center text-xs text-gray-600">
                        {c.guaranteeCount ? `${c.guaranteeCount}명` : '-'}
                      </td>
                      <td className="px-3 py-3 text-center text-xs text-gray-600">{c.customerSource || '-'}</td>
                      <td className="px-3 py-3 text-xs text-gray-600">{c.staffName || '-'}</td>
                      <td className="px-3 py-3 text-center text-xs text-gray-500">{formatDate(c.contractDt)}</td>
                      <td className="px-3 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full ${CONTRACT_STATUS_COLORS[c.contractStatus] || 'bg-gray-100 text-gray-700'}`}>
                          {c.contractStatus}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <button onClick={handleSms}
                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded p-1 cursor-pointer transition-colors">
                          <i className="ri-message-2-line text-base"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg text-sm cursor-pointer ${page === p ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-600'}`}>{p}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}

        {/* Bulk action bar */}
        {checkedIds.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
            <div className="max-w-screen-xl mx-auto px-8 py-3 flex items-center justify-between">
              <span className="text-sm text-gray-700">
                체크하신 고객 <span className="font-bold text-blue-600">{checkedIds.length}</span>건
              </span>
              <button onClick={handleBulkSms}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm font-medium cursor-pointer">
                <i className="ri-message-2-line"></i> 문자보내기
              </button>
            </div>
          </div>
        )}

        {/* 고객 등록 모달 */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">신규 고객 등록</h2>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <form onSubmit={handleCreate} className="p-6 space-y-6">
                {/* 계약/행사 구분 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-file-list-3-line text-blue-500"></i> 계약/행사 구분
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">계약구분 <span className="text-red-500">*</span></label>
                      <select value={form.contractStatus} onChange={(e) => setForm({ ...form, contractStatus: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="홀드">홀드</option>
                        <option value="가계약">가계약</option>
                        <option value="계약">계약</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">행사유형 <span className="text-red-500">*</span></label>
                      <select value={form.eventType} onChange={(e) => setForm({ ...form, eventType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* 고객 정보 - 예식 */}
                {isWedding ? (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <i className="ri-heart-line text-pink-500"></i> 신랑/신부 정보
                    </h3>
                    <p className="text-xs text-gray-400 mb-2 -mt-2">신랑 또는 신부 중 한 명의 이름과 연락처는 필수입니다</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">신랑 이름 <span className="text-red-500">*</span></label>
                        <input type="text" value={form.groomName} onChange={(e) => setForm({ ...form, groomName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">신랑 연락처 <span className="text-red-500">*</span></label>
                        <input type="tel" value={form.groomPhone} onChange={(e) => setForm({ ...form, groomPhone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="010-0000-0000" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">신부 이름 <span className="text-red-500">*</span></label>
                        <input type="text" value={form.brideName} onChange={(e) => setForm({ ...form, brideName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">신부 연락처 <span className="text-red-500">*</span></label>
                        <input type="tel" value={form.bridePhone} onChange={(e) => setForm({ ...form, bridePhone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="010-0000-0000" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <i className="ri-user-line text-blue-500"></i> 담당자 정보
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">담당자명 <span className="text-red-500">*</span></label>
                        <input type="text" value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">담당자 연락처 <span className="text-red-500">*</span></label>
                        <input type="tel" value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="010-0000-0000" />
                      </div>
                    </div>
                  </div>
                )}

                {/* 행사 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-building-2-line text-gray-400"></i> 행사 정보
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">예식홀 <span className="text-red-500">*</span></label>
                      <select value={form.venueIdx} onChange={(e) => setForm({ ...form, venueIdx: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        {venues.map(v => <option key={v.venueIdx} value={v.venueIdx}>{v.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">행사일 <span className="text-red-500">*</span></label>
                      <input type="date" value={form.eventDt} onChange={(e) => setForm({ ...form, eventDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">시간</label>
                      <input type="time" value={form.eventTime} onChange={(e) => setForm({ ...form, eventTime: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>

                {/* 식대/대관 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-restaurant-line text-gray-400"></i> 식대/대관 정보
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">식대메뉴</label>
                      <input type="text" value={form.menuName} onChange={(e) => setForm({ ...form, menuName: e.target.value })}
                        placeholder="예: 한식뷔페 A코스"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">식대단가 (원)</label>
                      <input type="number" value={form.mealPrice} onChange={(e) => setForm({ ...form, mealPrice: e.target.value })}
                        placeholder="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">보증인원</label>
                      <input type="number" value={form.guaranteeCount} onChange={(e) => setForm({ ...form, guaranteeCount: e.target.value })}
                        placeholder="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">대관료 (원)</label>
                      <input type="number" value={form.venueRentalFee} onChange={(e) => setForm({ ...form, venueRentalFee: e.target.value })}
                        placeholder="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>

                {/* 고객경로 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-route-line text-gray-400"></i> 고객경로
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">고객경로</label>
                      <select value={form.customerSource} onChange={(e) => setForm({ ...form, customerSource: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        {CUSTOMER_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">업체명</label>
                      <input type="text" value={form.vendorName} onChange={(e) => setForm({ ...form, vendorName: e.target.value })}
                        placeholder="업체명 입력"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>

                {/* 메모 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button type="button" onClick={() => setShowAddModal(false)}
                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium cursor-pointer">취소</button>
                  <button type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
